import { useEffect, useState } from 'react';
import {
  incidentDoctorTold,
  incidentFamilyTold,
  incidentTell,
  type ApiClient,
  type ApiError,
  type DoctorToldMethod,
  type IncidentActionResponse,
  type IncidentReport,
  type IncidentTellDraft,
} from '@aithora/shared';
import { ErrorNote } from '../../../ErrorNote';
import { useQuery } from '../../../useQuery';
import { A, sx } from '../parts';
import { HEAD, NOTE, chip, prim } from './styles';

/**
 * Tell the family and Tell the doctor on the charge nurse inbox (design P.vm inbox, famOpen):
 * tapping the button opens this step inside the report's card, never a bare stamp. It reads
 * GET /guardian/incident-reports/:id/tell (who to tell and a draft; writes nothing), then
 * records how it was done:
 *   - family: Send in the app (the Family Portal thread) or I called them (who, a note);
 *   - doctor: phone, fax or portal, who, and what the doctor said or ordered.
 * The draft's label is the server's: an Atlas draft says to check it, a template says it is
 * not Atlas. Sent in the app, each family member also gets a short alert on the channels
 * they chose (push, text, WhatsApp, email), with no health details.
 */

const PANEL = 'display:flex;flex-direction:column;gap:7px;padding:10px;border-radius:10px;background:rgba(47,92,151,.06);';
const FIELD = 'box-sizing:border-box;width:100%;padding:9px 11px;border-radius:9px;border:1px solid rgba(34,39,46,.2);background:#FFFFFF;font-family:inherit;font-size:16px;line-height:1.45;color:#22272E;resize:vertical;';
const INPUT = 'box-sizing:border-box;width:100%;min-height:36px;padding:0 10px;border-radius:9px;border:1px solid rgba(34,39,46,.2);background:#FFFFFF;font-family:inherit;font-size:16px;color:#22272E;';
const LINK = 'font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;';

/** The honesty tag on a draft: ATLAS DRAFT (check it) in blue, TEMPLATE in grey. */
export function DraftTag({ draft }: { draft: IncidentTellDraft }) {
  const atlas = draft.source === 'atlas';
  return (
    <div style={sx('display:flex;flex-direction:column;gap:3px;')}>
      <span
        data-testid="tell-draft-label"
        style={sx(
          `align-self:flex-start;font-size:14px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;border-radius:4px;padding:1px 6px;${atlas ? 'color:#2F5C97;border:1px solid rgba(47,92,151,.4);' : 'color:#54606C;border:1px solid rgba(84,96,108,.4);'}`,
        )}
      >
        {draft.label}
      </span>
      {draft.reason && <span style={sx(NOTE)}>{draft.reason}</span>}
    </div>
  );
}

function useTell(client: ApiClient, report: IncidentReport, who: 'family' | 'doctor') {
  return useQuery((signal) => incidentTell(client, { params: { id: report.id }, query: { who }, signal }), [client, report.id, who]);
}

export function TellFamilyPanel({ client, report, onDone, onCancel }: {
  client: ApiClient;
  report: IncidentReport;
  onDone: (res: IncidentActionResponse, toast: string) => void;
  onCancel: () => void;
}) {
  const q = useTell(client, report, 'family');
  const [text, setText] = useState('');
  const [mode, setMode] = useState<'in_app' | 'phone'>('in_app');
  const [whoId, setWhoId] = useState<string | null>(null);
  const [whoName, setWhoName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const d = q.data;
  useEffect(() => {
    if (!d) return;
    setText(d.draft.text);
    if (!d.methods.includes('in_app')) setMode('phone');
  }, [d]);

  const canApp = !!d && d.methods.includes('in_app') && !!text.trim();
  const canCall = !!whoName.trim();
  const go = async () => {
    if (busy || (mode === 'in_app' ? !canApp : !canCall)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await incidentFamilyTold(client, {
        params: { id: report.id },
        body: mode === 'in_app'
          ? { method: 'in_app', message: text.trim() }
          : { method: 'phone', spokenWith: whoName.trim(), ...(whoId ? { spokenWithUserId: whoId } : {}), ...(note.trim() ? { note: note.trim() } : {}) },
      });
      const n = res.notified.length;
      onDone(res, mode === 'in_app'
        ? `Sent in the app to ${n} family ${n === 1 ? 'member' : 'members'}. It is in the Truth Ledger.`
        : `Call recorded: spoke with ${whoName.trim()}. It is in the Truth Ledger.`);
    } catch (e) {
      setError(e as ApiError);
      setBusy(false);
    }
  };

  return (
    <div data-testid={`tell-family-${report.id}`} style={sx(PANEL)}>
      <div style={sx(HEAD)}>Tell the family</div>
      {q.error && <ErrorNote error={q.error} onRetry={q.reload} compact />}
      {q.loading && !d && <span style={sx(NOTE)}>Getting the family and a draft</span>}
      {d && (
        <>
          {d.noneOnRecord ? (
            <span role="note" style={sx('font-size:16px;font-weight:700;color:#8A5A00;line-height:1.45;')}>{d.noneOnRecord}</span>
          ) : (
            <span style={sx('font-size:16px;color:#22272E;line-height:1.45;')}>
              {'In the app: '}
              {d.family.map((p) => [p.name ?? 'Family member', p.phone].filter(Boolean).join(' · ')).join(', ')}
            </span>
          )}
          <DraftTag draft={d.draft} />
          <textarea value={text} onChange={(e) => setText(e.target.value)} aria-label="Message to the family" style={sx(`${FIELD}min-height:96px;`)} />
          <div role="group" aria-label="How the family is told" style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
            {d.methods.includes('in_app') && (
              <A style={chip(mode === 'in_app')} onClick={() => setMode('in_app')}>
                Send in the app
              </A>
            )}
            <A style={chip(mode === 'phone')} onClick={() => setMode('phone')}>
              I called them
            </A>
          </div>
          {mode === 'in_app' && (
            <span style={sx(NOTE)}>The family reads it in their Aithora app. Each family member also gets a short alert, with no health details, on the channels they chose.</span>
          )}
          {mode === 'phone' && (
            <>
              <div style={sx(HEAD)}>Who you spoke with</div>
              {d.family.length > 0 && (
                <div style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
                  {d.family.map((p) => (
                    <A
                      key={p.userId}
                      style={chip(whoId === p.userId)}
                      onClick={() => {
                        setWhoId(p.userId);
                        setWhoName(p.name ?? '');
                      }}
                    >
                      {p.name ?? 'Family member'}
                    </A>
                  ))}
                </div>
              )}
              <input
                value={whoName}
                onChange={(e) => {
                  setWhoName(e.target.value);
                  setWhoId(null);
                }}
                placeholder="Their name"
                aria-label="Who you spoke with"
                style={sx(INPUT)}
              />
              <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="A note, if you want one" aria-label="Note about the call" style={sx(`${FIELD}min-height:56px;`)} />
            </>
          )}
          {error && <ErrorNote error={error} compact />}
          <div style={sx('display:flex;align-items:center;gap:12px;flex-wrap:wrap;')}>
            <A style={prim(mode === 'in_app' ? canApp : canCall)} onClick={() => void go()} disabled={busy || (mode === 'in_app' ? !canApp : !canCall)}>
              {mode === 'in_app' ? 'Approve and send' : 'Record the call'}
            </A>
            <A style={LINK} onClick={onCancel}>
              Cancel
            </A>
          </div>
        </>
      )}
    </div>
  );
}

const DOCTOR_METHODS: [string, DoctorToldMethod][] = [['Phone', 'phone'], ['Fax', 'fax'], ['Portal', 'portal']];

export function TellDoctorPanel({ client, report, onDone, onCancel }: {
  client: ApiClient;
  report: IncidentReport;
  onDone: (res: IncidentActionResponse, toast: string) => void;
  onCancel: () => void;
}) {
  const q = useTell(client, report, 'doctor');
  const [sbar, setSbar] = useState('');
  const [method, setMethod] = useState<DoctorToldMethod | ''>('');
  const [pracId, setPracId] = useState<string | null>(null);
  const [whoName, setWhoName] = useState('');
  const [orders, setOrders] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const d = q.data;
  useEffect(() => {
    if (!d) return;
    setSbar(d.draft.text);
    if (d.practitioners.length === 1) {
      setPracId(d.practitioners[0].practitionerId);
      setWhoName(d.practitioners[0].name);
    }
  }, [d]);

  const ready = !!method && !!whoName.trim();
  const go = async () => {
    if (busy || !ready || !method) return;
    setBusy(true);
    setError(null);
    try {
      const res = await incidentDoctorTold(client, {
        params: { id: report.id },
        body: { method, spokenWith: whoName.trim(), ...(pracId ? { practitionerId: pracId } : {}), ...(orders.trim() ? { orders: orders.trim() } : {}) },
      });
      onDone(res, `Doctor told: ${whoName.trim()}. It is in the Truth Ledger.`);
    } catch (e) {
      setError(e as ApiError);
      setBusy(false);
    }
  };

  return (
    <div data-testid={`tell-doctor-${report.id}`} style={sx(PANEL)}>
      <div style={sx(HEAD)}>Tell the doctor</div>
      {q.error && <ErrorNote error={q.error} onRetry={q.reload} compact />}
      {q.loading && !d && <span style={sx(NOTE)}>Getting the doctor and a summary</span>}
      {d && (
        <>
          {d.noneOnRecord ? (
            <span role="note" style={sx('font-size:16px;font-weight:700;color:#8A5A00;line-height:1.45;')}>{d.noneOnRecord}</span>
          ) : (
            <div style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
              {d.practitioners.map((p) => (
                <A
                  key={p.practitionerId}
                  style={chip(pracId === p.practitionerId)}
                  onClick={() => {
                    setPracId(p.practitionerId);
                    setWhoName(p.name);
                  }}
                  title={p.phone ? `Phone ${p.phone}` : 'No phone on record'}
                >
                  {p.name}
                </A>
              ))}
            </div>
          )}
          {!d.noneOnRecord && d.practitioners.every((p) => !p.phone) && <span style={sx(NOTE)}>No phone is on record for the doctor in Aithora.</span>}
          <div style={sx(HEAD)}>Read this on the call (SBAR)</div>
          <DraftTag draft={d.draft} />
          <textarea value={sbar} onChange={(e) => setSbar(e.target.value)} aria-label="SBAR summary" style={sx(`${FIELD}min-height:120px;`)} />
          <div style={sx(HEAD)}>How</div>
          <div role="group" aria-label="How the doctor was told" style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
            {DOCTOR_METHODS.map(([l, v]) => (
              <A key={v} style={chip(method === v)} onClick={() => setMethod(v)}>
                {l}
              </A>
            ))}
          </div>
          <div style={sx(HEAD)}>Who you spoke with</div>
          <input
            value={whoName}
            onChange={(e) => {
              setWhoName(e.target.value);
              setPracId(null);
            }}
            placeholder="Their name"
            aria-label="Who you spoke with"
            style={sx(INPUT)}
          />
          <textarea value={orders} onChange={(e) => setOrders(e.target.value)} placeholder="What the doctor said or ordered (optional)" aria-label="What the doctor said or ordered" style={sx(`${FIELD}min-height:56px;`)} />
          {error && <ErrorNote error={error} compact />}
          <div style={sx('display:flex;align-items:center;gap:12px;flex-wrap:wrap;')}>
            <A style={prim(ready)} onClick={() => void go()} disabled={busy || !ready}>
              Record doctor told
            </A>
            <A style={LINK} onClick={onCancel}>
              Cancel
            </A>
          </div>
        </>
      )}
    </div>
  );
}
